import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';
import { onboardingTasks } from '@ourbranch/onboarding-tasks';

import { AuthContext } from 'core/components/auth';
import { Label } from 'core/components/label';
import { brandConstants, isBranchEnv } from 'brand-config';
import { useStore } from 'core/store/store.mobx';
import { useRenameDocument } from 'core/hooks/use-rename-document';
import { dateTimeFormatter } from 'core/helpers/formatters';
import { Loading, SelectableRow, Table } from 'core';
import Section from 'core/components/section';
import { Button } from 'core/components/button';
import { Card } from 'core/components/card';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { useToast } from 'core/components/toast';
import viewIcon from '../../../../assets/view.svg';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import editIcon from 'core/assets/svg/edit.svg';
import copyIcon from 'core/assets/svg/copy.svg';
import reviewImagesIcon from 'core/assets/svg/ic-review-images.svg';
import { CopyInternalDocToPolicyDocsModal } from '../copy-internal-doc-to-policy-docs-modal';
import { ConfirmDeleteInternalDocumentModal } from '../confirm-delete-internal-document-modal';
import { AssignPolicyMenu } from '../assign-policy-menu';
import { DocumentImagePreviewModal, isRenderableImage } from 'common/components/document-image-preview-modal';
import { RenameDocumentModal } from 'core/components/rename-document-modal';
import { DropzoneContainer } from 'core/components/dropzone';
import useStyles from '../../documents.styles';

const InternalDocuments = observer(
  ({
    fetchDocuments,
    sortDocuments,
    getDocName,
    downloadDocument,
    getDocumentSignedUrl,
    uploaded,
    uploadFile,
    percent,
    error,
    deleting,
    file,
    policies
  }) => {
    const classes = useStyles();
    const toast = useToast();
    const [copyDocsModalOpen, setCopyDocsModalOpen] = useState(false);
    const [documentToCopy, setDocumentToCopy] = useState(null);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [documentToRename, setDocumentToRename] = useState(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);

    const {
      account: {
        id: accountId,
        policies: { documents, loadingDocuments: loading, deleteInternalDocument, setDocumentPolicy },
        tasks
      }
    } = useStore();

    const { canUploadDocuments, canDeleteInternalDocuments, isInternalAgent } = useContext(AuthContext);
    const renameDocument = useRenameDocument({ accountId, getDocName, fetchDocuments });

    const onRenameConfirm = useCallback(
      async (label) => {
        if (!documentToRename) {
          return;
        }
        await renameDocument(documentToRename.path, label);
      },
      [documentToRename, renameDocument]
    );

    const onDeleteConfirm = useCallback(async () => {
      if (!documentToDelete) {
        return;
      }
      try {
        await deleteInternalDocument(accountId, documentToDelete.path);
        await fetchDocuments();
        setDocumentToDelete(null);
      } catch {
        toast.notify({
          type: 'error',
          message: `Couldn't delete ${getDocName(documentToDelete.path)}`
        });
        setDocumentToDelete(null);
      }
    }, [accountId, deleteInternalDocument, documentToDelete, fetchDocuments, getDocName, toast]);

    const assignPolicy = useCallback(
      async (doc, policyId) => {
        try {
          await setDocumentPolicy(accountId, doc.path, policyId);
          await fetchDocuments();
        } catch {
          toast.notify({
            type: 'error',
            message: `Couldn't update the policy for ${getDocName(doc.path)}`
          });
        }
      },
      [accountId, fetchDocuments, getDocName, setDocumentPolicy, toast]
    );

    const documentToTaskMap = new Map();
    if (tasks && tasks.length > 0) {
      tasks.forEach((task) => {
        if (task.data) {
          const parsedData = typeof task.data === 'string' ? JSON.parse(task.data) : task.data;

          if (parsedData?.files) {
            parsedData.files.forEach((file) => {
              if (file?.documentUrl) {
                documentToTaskMap.set(file.documentUrl, task.taskName);
              }
            });
          }

          // Backward compatibility - support old single documentUrl field for tasks that will not
          // have a files array but still the doc will need to show
          if (parsedData?.documentUrl) {
            if (!documentToTaskMap.has(parsedData.documentUrl)) {
              documentToTaskMap.set(parsedData.documentUrl, task.taskName);
            }
          }
        }
      });
    }

    const getTaskName = useCallback(
      (documentPath) => {
        const mappedTaskName = documentToTaskMap.get(documentPath);
        if (mappedTaskName) {
          return mappedTaskName;
        }

        // special case for spp standalone task
        if (documentPath?.includes('inventory_') && documentPath?.endsWith('_spp.pdf')) {
          return onboardingTasks.NeedScheduledPersonalPropertyImages;
        }

        // Operator uploads (internal/ namespace) aren't tied to an onboarding task,
        // so label them as a manual upload rather than the muted "Not specified".
        if (documentPath?.startsWith('internal/')) {
          return 'Manual Upload';
        }

        return 'Not specified';
      },
      [documentToTaskMap]
    );

    const hasPreviewableImages = !loading && documents?.internal?.some((d) => isRenderableImage(d.path));

    // On mutual brands (CFM, Forreston, Oakwood), Internal Documents (inspections, underwriting notes,
    // operator uploads) are staff-only: mutual agents use this same customer view, so agency users
    // (isAgency → isInternalAgent === false) get no section at all — no list, no upload (#35462).
    // Branch/BIX agency agents DO reach this view and rely on the section to see files they upload via
    // onboarding tasks, so on Branch it renders for everyone, as it did before #35481.
    if (!isBranchEnv && !isInternalAgent) {
      return null;
    }

    return (
      <>
        <Section
          title="Internal Documents"
          rightLabel="Total"
          rightValue={`${loading || !documents ? '...' : documents.internal.length} documents`}
          titleAction={
            hasPreviewableImages ? (
              <Button
                disableFocusRipple
                className={classes.previewImagesButton}
                onClick={() => setPreviewModalOpen(true)}
                variant="text"
                color="secondary"
              >
                Preview all images
                <img alt="review images icon" className={classes.previewImagesIcon} src={reviewImagesIcon} />
              </Button>
            ) : null
          }
        >
          {loading || !documents ? (
            <Loading />
          ) : (
            <Card>
              {documents.internal.length || uploaded.length ? (
                <Table
                  selfContained={false}
                  header={
                    <>
                      <TableCell className={classes.fileNameHeader}>FILE NAME</TableCell>
                      <TableCell className={classes.documentTypeHeader}>DOCUMENT TYPE</TableCell>
                      <TableCell className={classes.policyHeader}>POLICY</TableCell>
                      <TableCell className={classes.lastUpdateHeader}>LAST UPDATE</TableCell>
                      {/* need the following empty tablecell header for the view or copy action buttons */}
                      <TableCell />
                    </>
                  }
                  body={
                    <>
                      {sortDocuments(documents.internal.slice() || []).map((d) => {
                        const fileName = getDocName(d.path);
                        const displayName = d.displayName || fileName;
                        const taskName = getTaskName(d.path);
                        // Only operator uploads (internal/ namespace) are relabelable; task docs
                        // surfaced here via other paths are rejected by the backend.
                        const isRelabelable = d.path.startsWith('internal/');
                        // Copying publishes the file to the member, so it is internal-staff only; the
                        // API enforces the same rule and only copies internal/ (or offer/) sources (#36196).
                        const canCopyToPolicyDocs = isInternalAgent && canUploadDocuments && isRelabelable;
                        return (
                          <TableRow key={d.path} id={d.path}>
                            <TableCell className={classes.docTitleCell}>
                              <Tooltip title={fileName} placement="top">
                                <span className={classes.truncatedText}>{displayName}</span>
                              </Tooltip>
                              {d.displayName ? (
                                <span className={classNames(classes.truncatedText, classes.notSpecifiedText)}>
                                  {fileName}
                                </span>
                              ) : null}
                            </TableCell>
                            <TableCell className={classes.documentTypeCell}>
                              <Tooltip title={taskName} placement="top">
                                <span
                                  className={classNames(classes.truncatedText, {
                                    [classes.notSpecifiedText]: taskName === 'Not specified'
                                  })}
                                >
                                  {taskName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.policyCell}>
                              {d.policyId ? (
                                <Tooltip title={d.policyId} placement="top">
                                  <span className={classes.truncatedText}>{d.policyId}</span>
                                </Tooltip>
                              ) : (
                                <span className={classNames(classes.truncatedText, classes.notSpecifiedText)}>N/A</span>
                              )}
                            </TableCell>
                            <TableCell className={classes.lastUpdateCell}>
                              <span className={classes.smallText}>
                                {dateTimeFormatter(d.lastModified).split(' @ ')[0]}
                                <br />@ {dateTimeFormatter(d.lastModified).split(' @ ')[1]}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className={classes.rowActions}>
                                <IconButtonWithTooltip
                                  tooltip="View file"
                                  onClick={() => downloadDocument(d.path)}
                                  icon={viewIcon}
                                  alt="view document"
                                  iconClassName={classes.rowActionButton}
                                  imgClassName={classes.rowActionIcon}
                                />
                                {canCopyToPolicyDocs && (
                                  <IconButtonWithTooltip
                                    tooltip="Copy file to policy documents"
                                    onClick={() => {
                                      setDocumentToCopy(d);
                                      setCopyDocsModalOpen(true);
                                    }}
                                    icon={copyIcon}
                                    alt="copy internal doc to member"
                                    iconClassName={classes.rowActionButton}
                                    imgClassName={classes.rowActionIcon}
                                  />
                                )}
                                {canUploadDocuments && isRelabelable && (
                                  <AssignPolicyMenu
                                    policies={policies}
                                    currentPolicyId={d.policyId || ''}
                                    onAssign={(policyId) => assignPolicy(d, policyId)}
                                    iconClassName={classes.rowActionButton}
                                    imgClassName={classes.rowActionIcon}
                                  />
                                )}
                                {canUploadDocuments && isRelabelable && (
                                  <IconButtonWithTooltip
                                    tooltip="Rename file"
                                    onClick={() => setDocumentToRename(d)}
                                    icon={editIcon}
                                    alt="rename document"
                                    iconClassName={classes.rowActionButton}
                                    imgClassName={classes.rowActionIcon}
                                  />
                                )}
                                {canDeleteInternalDocuments && (
                                  <IconButtonWithTooltip
                                    tooltip="Delete file"
                                    onClick={() => setDocumentToDelete(d)}
                                    icon={deleteIcon}
                                    alt="delete document"
                                    iconClassName={classes.rowActionButton}
                                    imgClassName={classes.rowActionIcon}
                                  />
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {sortDocuments(uploaded).map((d) => {
                        const fileName = getDocName(d.path);
                        const taskName = getTaskName(d.path);
                        return (
                          <SelectableRow key={d.path} id={d.path} onClick={downloadDocument}>
                            <TableCell className={classNames(classes.highlighted, classes.docTitleCell)}>
                              <Tooltip title={fileName} placement="top">
                                <span className={classes.truncatedText}>{fileName}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classNames(classes.highlighted, classes.documentTypeCell)}>
                              <Tooltip title={taskName} placement="top">
                                <span
                                  className={classNames(classes.truncatedText, {
                                    [classes.notSpecifiedText]: taskName === 'Not specified'
                                  })}
                                >
                                  {taskName}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classNames(classes.highlighted, classes.policyCell)}>
                              <span className={classNames(classes.truncatedText, classes.notSpecifiedText)}>…</span>
                            </TableCell>
                            <TableCell className={classNames(classes.highlighted, classes.lastUpdateCell)}>
                              <span className={classes.smallText}>
                                {dateTimeFormatter(d.lastModified).split(' @ ')[0]}
                                <br />@ {dateTimeFormatter(d.lastModified).split(' @ ')[1]}
                              </span>
                            </TableCell>
                          </SelectableRow>
                        );
                      })}
                    </>
                  }
                />
              ) : (
                <p className={classes.noDocs}>No internal documents for this member.</p>
              )}
              {canUploadDocuments ? (
                <DropzoneContainer
                  onUpload={uploadFile}
                  progress={percent}
                  error={error}
                  deleting={deleting}
                  uploading={file ? file.path : undefined}
                />
              ) : (
                <div className={classes.cantUploadContainer}>
                  <Label className={classes.cantUpload}>
                    To provide a document for this member, please send the document to Branch at{' '}
                    <a
                      className={classes.support}
                      href={`mailto:${brandConstants.SUPPORT_EMAIL}`}
                      rel="noopener noreferrer"
                    >
                      {brandConstants.SUPPORT_EMAIL}
                    </a>
                  </Label>
                </div>
              )}
            </Card>
          )}
        </Section>
        {copyDocsModalOpen && (
          <CopyInternalDocToPolicyDocsModal
            open={copyDocsModalOpen}
            document={documentToCopy}
            refetchDocuments={fetchDocuments}
            onClose={() => {
              setCopyDocsModalOpen(false);
            }}
          />
        )}
        {documentToDelete && (
          <ConfirmDeleteInternalDocumentModal
            fileName={getDocName(documentToDelete.path)}
            onConfirm={onDeleteConfirm}
            onClose={() => setDocumentToDelete(null)}
          />
        )}
        {documentToRename && (
          <RenameDocumentModal
            fileName={getDocName(documentToRename.path)}
            currentLabel={documentToRename.displayName || ''}
            onSave={onRenameConfirm}
            onClose={() => setDocumentToRename(null)}
          />
        )}
        {previewModalOpen && documents && (
          <DocumentImagePreviewModal
            open={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            documents={documents.internal}
            getDocName={getDocName}
            getSignedUrl={getDocumentSignedUrl}
            onRename={renameDocument}
            canRenameDocument={(doc) => canUploadDocuments && doc.path.startsWith('internal/')}
            title="Internal document images"
          />
        )}
      </>
    );
  }
);

InternalDocuments.propTypes = {
  sortDocuments: PropTypes.func.isRequired,
  fetchDocuments: PropTypes.func.isRequired,
  getDocName: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
  getDocumentSignedUrl: PropTypes.func.isRequired,
  uploaded: PropTypes.array,
  uploadFile: PropTypes.func.isRequired,
  percent: PropTypes.number,
  error: PropTypes.string,
  deleting: PropTypes.bool,
  file: PropTypes.object,
  policies: PropTypes.array
};

InternalDocuments.defaultProps = {
  percent: 0,
  uploaded: [],
  deleting: false,
  error: undefined,
  file: undefined,
  policies: []
};

export default InternalDocuments;
